import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Activity,
  Clock,
  Eye,
  Edit,
  Trash2,
  Navigation,
  Download,
  Search,
  Filter,
  Send,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  User,
  Globe,
  Monitor,
  FileText,
  Users,
} from 'lucide-react';
import backendApi from '../../services/backendApi';

interface ActivityLog {
  id: number;
  user: {
    id: number;
    username: string;
    full_name: string;
  };
  activity_type: string;
  activity_type_display: string;
  page_url: string;
  page_title: string;
  action_description: string;
  time_spent_seconds: number;
  time_spent_formatted: string;
  created_at: string;
  metadata?: any;
  form_data?: any;
  ip_address?: string;
  session_id?: string;
}

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

const UserActivityPage: React.FC = () => {
  const [activities, setActivities] = useState<ActivityLog[]>([]);
  const [users, setUsers] = useState<Array<{ id: number; username: string; first_name: string; last_name: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  const [filterType, setFilterType] = useState<string>('');
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [selectedUsername, setSelectedUsername] = useState<string>('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [isRealTime, setIsRealTime] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'detailed' | 'compact'>('detailed');
  const [availableActivityTypes, setAvailableActivityTypes] = useState<string[]>([]);
  const [activityTypeCounts, setActivityTypeCounts] = useState<Record<string, number>>({});

  const activityIcons: Record<string, React.ComponentType<any>> = {
    page_visit: Eye,
    form_submit: Send,
    data_view: Eye,
    data_edit: Edit,
    data_delete: Trash2,
    navigation: Navigation,
    download: Download,
    export: Download,
    search: Search,
    filter: Filter,
    other: Activity,
  };

  const activityColors: Record<string, string> = {
    page_visit: '#0084CF',
    form_submit: '#10B981',
    data_view: '#0066A3',
    data_edit: '#F59E0B',
    data_delete: '#EF4444',
    navigation: '#0084CF',
    download: '#0084CF',
    export: '#0084CF',
    search: '#0066A3',
    filter: '#0084CF',
    other: '#6B7280',
  };

  const fetchActivities = useCallback(async (showLoading = true) => {
    if (showLoading) {
      setLoading(true);
    }
    try {
      const params: any = {
        page,
        page_size: 50,
      };
      if (filterType) {
        params.activity_type = filterType;
      }
      if (selectedUserId) {
        params.user_id = selectedUserId;
      }
      if (selectedUsername) {
        params.username = selectedUsername;
      }

      const response = await backendApi.getAdminUserActivityLogs(params) as ApiResponse<{
        logs: ActivityLog[];
        count: number;
        page: number;
        page_size: number;
        total_pages: number;
        users: Array<{ id: number; username: string; first_name: string; last_name: string }>;
        available_activity_types?: string[];
        activity_type_counts?: Record<string, number>;
      }>;

      if (response && response.success && response.data) {
        const logs = response.data.logs || [];
        setActivities(logs);
        setTotalPages(response.data.total_pages || 1);
        setTotalCount(response.data.count || 0);
        if (response.data.users) {
          setUsers(response.data.users);
        }
        if (response.data.available_activity_types) {
          setAvailableActivityTypes(response.data.available_activity_types);
        }
        if (response.data.activity_type_counts) {
          setActivityTypeCounts(response.data.activity_type_counts);
        }
        setLastUpdate(new Date());
        setError(null);
      } else {
        setError(response?.message || response?.error || 'Unknown error');
      }
    } catch (error: any) {
      console.error('Error fetching activities:', error);
      setError(error.message || 'Failed to fetch activities');
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [filterType, page, selectedUserId, selectedUsername]);

  useEffect(() => {
    void fetchActivities(true);
  }, [fetchActivities]);

  useEffect(() => {
    if (!isRealTime) {
      return;
    }

    let timeoutId: NodeJS.Timeout | null = null;
    let isMounted = true;
    let currentInterval = 10000;
    let consecutiveErrors = 0;

    const pollActivities = async () => {
      if (!isMounted) return;

      try {
        const token = sessionStorage.getItem('access_token');
        if (!token) {
          consecutiveErrors++;
          currentInterval = Math.min(10000 * Math.pow(2, consecutiveErrors), 60000);
          if (isMounted) timeoutId = setTimeout(pollActivities, currentInterval);
          return;
        }

        await fetchActivities(false);
        consecutiveErrors = 0;
        currentInterval = 10000;
        
        if (isMounted) {
          timeoutId = setTimeout(pollActivities, currentInterval);
        }
      } catch (error: any) {
        if (error?.message?.includes('401')) {
          consecutiveErrors++;
          currentInterval = Math.min(10000 * Math.pow(2, consecutiveErrors), 60000);
        } else {
          currentInterval = Math.min(currentInterval + 5000, 30000);
        }
        if (isMounted) {
          timeoutId = setTimeout(pollActivities, currentInterval);
        }
      }
    };

    timeoutId = setTimeout(() => {
      if (isMounted) pollActivities();
    }, currentInterval);

    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [fetchActivities, isRealTime]);

  const toggleExpand = (id: number) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleString();
  };

  const getActivityIcon = (activityType: string) => activityIcons[activityType] || Activity;

  const getActivityColor = (activityType: string) => activityColors[activityType] || '#6B7280';

  const activityTypeDisplayNames: Record<string, string> = {
    page_visit: 'Page Visit',
    form_submit: 'Form Submission',
    data_view: 'Data View',
    data_edit: 'Data Edit',
    data_delete: 'Data Delete',
    navigation: 'Navigation',
    download: 'Download',
    export: 'Export',
    search: 'Search',
    filter: 'Filter',
    other: 'Other',
  };

  const getActivityTypeDisplayName = (type: string): string => {
    return activityTypeDisplayNames[type] || type.split('_').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const getPageName = (url: string, pageTitle?: string): string => {
    if (pageTitle) {
      return pageTitle;
    }
    if (!url) return 'Unknown Page';

    try {
      let path = url.replace(/^https?:\/\/[^/]+/, '');

      const routeMap: Record<string, string> = {
        '/admin': 'Admin Dashboard',
        '/admin/users': 'User Management',
        '/admin/sites': 'Site Management',
        '/admin/customers': 'Customer Management',
        '/admin/user-activity': 'User Activity Logs',
        '/admin/security': 'Security Monitoring',
        '/dashboard': 'Analytics Dashboard',
        '/entry': 'DGR Manual Entry',
        '/upload': 'DGR Upload',
        '/alerts': 'Alerts Portal',
        '/simulation': 'Simulation-on-Demand',
        '/inventory': 'Inventory Management',
        '/vendors': 'Vendor Management',
        '/login': 'Login Page',
        '/navigation': 'Navigation Page',
      };

      for (const [route, name] of Object.entries(routeMap)) {
        if (path.startsWith(route)) {
          const siteMatch = path.match(new RegExp(`^${route}/([^/]+)`));
          if (siteMatch) {
            return `${name} (${siteMatch[1]})`;
          }
          return name;
        }
      }

      const parts = path.split('/').filter((part) => part && part !== 'undefined');
      if (parts.length > 0) {
        const lastPart = parts[parts.length - 1];
        return lastPart.split('-').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
      }

      return path || 'Unknown Page';
    } catch {
      return url || 'Unknown Page';
    }
  };

  const totalTimeSpent = useMemo(() => {
    return activities.reduce((sum, activity) => sum + (activity.time_spent_seconds || 0), 0);
  }, [activities]);

  const formatTotalTime = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins}m ${secs}s`;
    }
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${mins}m`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center admin-shell">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#0084CF]/30 border-t-[#0084CF] rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading user activity...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 lg:pt-4 pb-8 px-2 sm:px-3 lg:px-4">
      <div className="max-w-7xl mx-auto space-y-6">
        <motion.section initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="admin-hero p-4 sm:p-4 lg:p-5">
          <div className="admin-toolbar">
            <div className="space-y-2">
              <p className="admin-eyebrow">User activity</p>
              <div className="flex items-start gap-4">
                <div className="hidden sm:flex w-11 h-11 rounded-[16px] bg-[linear-gradient(135deg,#0084CF,#1C4D88)] items-center justify-center text-white shadow-[0_12px_20px_rgba(0,132,207,0.2)]">
                  <Activity size={22} />
                </div>
                <div>
                  <h1 className="admin-page-title">A cleaner activity stream for user movement, page visits, and form actions.</h1>
                  <p className="admin-page-subtitle mt-2">This page now follows the new admin shell and converts activity tracking into scan-friendly row entries with optional inline detail expansion.</p>
                  <div className="flex flex-wrap gap-2 mt-4">
                    <span className="admin-pill admin-pill-info">
                      <Activity size={12} />
                      {isRealTime ? 'Live updates enabled' : 'Manual refresh mode'}
                    </span>
                    {lastUpdate && (
                      <span className="admin-pill admin-pill-muted">
                        <Clock size={12} />
                        Updated {lastUpdate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <button onClick={() => setViewMode(viewMode === 'detailed' ? 'compact' : 'detailed')} className="admin-button admin-button-ghost">
                {viewMode === 'detailed' ? 'Dense rows' : 'Detailed rows'}
              </button>
              <button onClick={() => setIsRealTime((value) => !value)} className={`admin-button ${isRealTime ? 'admin-button-secondary' : 'admin-button-ghost'}`}>
                <Activity size={16} />
                {isRealTime ? 'Live sync' : 'Paused'}
              </button>
              <button onClick={() => fetchActivities(true)} className="admin-button admin-button-primary">
                <RefreshCw size={16} />
                Refresh
              </button>
            </div>
          </div>

          <div className="admin-stat-strip mt-6">
            <div className="admin-stat-card">
              <p className="admin-stat-label">Activities</p>
              <p className="admin-stat-value">{totalCount.toLocaleString()}</p>
              <p className="admin-stat-hint">All recorded activity events</p>
            </div>
            <div className="admin-stat-card">
              <p className="admin-stat-label">Time spent</p>
              <p className="admin-stat-value">{formatTotalTime(totalTimeSpent)}</p>
              <p className="admin-stat-hint">Accumulated time across loaded entries</p>
            </div>
            <div className="admin-stat-card">
              <p className="admin-stat-label">Active users</p>
              <p className="admin-stat-value">{users.length}</p>
              <p className="admin-stat-hint">Users represented in activity logs</p>
            </div>
            <div className="admin-stat-card">
              <p className="admin-stat-label">Page</p>
              <p className="admin-stat-value">{page}</p>
              <p className="admin-stat-hint">Page {page} of {totalPages}</p>
            </div>
            <div className="admin-stat-card">
              <p className="admin-stat-label">Visible now</p>
              <p className="admin-stat-value">{activities.length}</p>
              <p className="admin-stat-hint">Entries on the current page</p>
            </div>
          </div>
        </motion.section>

        <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="admin-section">
          <div className="admin-section-header">
            <div>
              <h2 className="admin-section-title">Filter activity feed</h2>
              <p className="admin-section-copy">Narrow the stream by user, activity type, or username search.</p>
            </div>
          </div>
          <div className="admin-section-body">
            <div className="admin-filter-grid">
              <div>
                <label className="block text-xs font-bold text-[#6A7A92] uppercase tracking-[0.16em] mb-2">Filter by user</label>
                <select
                  value={selectedUserId}
                  onChange={(e) => {
                    setSelectedUserId(e.target.value);
                    setPage(1);
                  }}
                  className="admin-select"
                >
                  <option value="">All users</option>
                  {users.map((currentUser) => (
                    <option key={currentUser.id} value={currentUser.id}>
                      {currentUser.username} ({currentUser.first_name} {currentUser.last_name})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-[#6A7A92] uppercase tracking-[0.16em] mb-2">Search username</label>
                <div className="admin-search-wrap">
                  <Search size={18} />
                  <input
                    type="text"
                    value={selectedUsername}
                    onChange={(e) => {
                      setSelectedUsername(e.target.value);
                      setPage(1);
                    }}
                    placeholder="Enter username"
                    className="admin-input"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-[#6A7A92] uppercase tracking-[0.16em] mb-2">Activity type</label>
                <select
                  value={filterType}
                  onChange={(e) => {
                    setFilterType(e.target.value);
                    setPage(1);
                  }}
                  className="admin-select"
                >
                  <option value="">All activities</option>
                  {availableActivityTypes.length > 0 ? (
                    availableActivityTypes.map((type) => {
                      const count = activityTypeCounts[type] || 0;
                      return (
                        <option key={type} value={type}>
                          {getActivityTypeDisplayName(type)} {count > 0 ? `(${count})` : ''}
                        </option>
                      );
                    })
                  ) : (
                    <>
                      <option value="page_visit">Page Visit</option>
                      <option value="form_submit">Form Submission</option>
                      <option value="data_view">Data View</option>
                      <option value="data_edit">Data Edit</option>
                      <option value="data_delete">Data Delete</option>
                      <option value="navigation">Navigation</option>
                      <option value="download">Download</option>
                      <option value="export">Export</option>
                      <option value="search">Search</option>
                      <option value="filter">Filter</option>
                      <option value="other">Other</option>
                    </>
                  )}
                </select>
              </div>
            </div>
          </div>
        </motion.section>

        <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="admin-section">
          <div className="admin-section-header">
            <div>
              <h2 className="admin-section-title">Activity stream</h2>
              <p className="admin-section-copy">Each activity is shown as a horizontal record with expandable diagnostic detail.</p>
            </div>
            {error ? <span className="admin-pill admin-pill-danger">Feed error</span> : <span className="admin-pill admin-pill-success">{activities.length} loaded</span>}
          </div>
          <div className="admin-section-body">
            {error && (
              <div className="mb-4 rounded-[20px] p-4 border border-[rgba(181,71,93,0.18)] bg-[rgba(181,71,93,0.08)]">
                <p className="text-sm text-[#8C2F45]"><strong>Error:</strong> {error}</p>
                <button onClick={() => { setError(null); void fetchActivities(true); }} className="mt-3 admin-button admin-button-ghost">Retry</button>
              </div>
            )}

            {activities.length === 0 ? (
              <div className="admin-empty">No activities matched the current filters.</div>
            ) : (
              <div className="admin-row-list">
                {activities.map((activity, index) => {
                  const Icon = getActivityIcon(activity.activity_type);
                  const color = getActivityColor(activity.activity_type);
                  const isExpanded = expandedItems.has(activity.id);

                  return (
                    <Fragment key={activity.id}>
                      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(index, 8) * 0.02 }} className="admin-row">
                        <div className="admin-row-leading">
                          <div className="admin-row-icon" style={{ background: `${color}20`, color }}>
                            <Icon size={18} />
                          </div>
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="admin-tag" style={{ backgroundColor: color, color: '#ffffff' }}>{activity.activity_type_display}</span>
                              <p className="admin-row-title truncate">{activity.user?.username || 'Unknown user'}</p>
                              {activity.user?.full_name && activity.user.full_name !== activity.user?.username && (
                                <span className="text-sm text-[#6A7A92] truncate">({activity.user.full_name})</span>
                              )}
                            </div>
                            <p className="admin-row-subtitle truncate mt-2">{getPageName(activity.page_url || '', activity.page_title)}</p>
                            {viewMode === 'detailed' && activity.action_description && (
                              <p className="text-sm text-[#4E637D] mt-2 leading-6">{activity.action_description}</p>
                            )}
                            <div className="admin-row-tags">
                              <span className="admin-tag">{formatDate(activity.created_at)}</span>
                              {activity.time_spent_seconds > 0 && <span className="admin-tag">{activity.time_spent_formatted}</span>}
                              {activity.ip_address && <span className="admin-tag">{activity.ip_address}</span>}
                            </div>
                          </div>
                        </div>

                        <div className="admin-row-meta">
                          <div className="admin-meta-block">
                            <p className="admin-meta-label">Page</p>
                            <p className="admin-meta-value">{getPageName(activity.page_url || '', activity.page_title)}</p>
                          </div>
                          <div className="admin-meta-block">
                            <p className="admin-meta-label">Timestamp</p>
                            <p className="admin-meta-value">{new Date(activity.created_at).toLocaleString()}</p>
                          </div>
                          <div className="admin-meta-block">
                            <p className="admin-meta-label">Time spent</p>
                            <p className="admin-meta-value">{activity.time_spent_formatted || '0s'}</p>
                          </div>
                          <div className="admin-meta-block">
                            <p className="admin-meta-label">Source</p>
                            <p className="admin-meta-value">{activity.ip_address || 'No IP recorded'}</p>
                          </div>
                        </div>

                        <div className="admin-row-actions">
                          <button onClick={() => toggleExpand(activity.id)} className="admin-icon-button" title={isExpanded ? 'Hide details' : 'Show details'}>
                            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </button>
                        </div>
                      </motion.div>

                      {isExpanded && (
                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="rounded-[20px] border border-[rgba(28, 77, 136,0.1)] bg-[rgba(246,251,255,0.88)] px-5 py-4 ml-0 lg:ml-3">
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <div className="rounded-[18px] border border-[rgba(28, 77, 136,0.08)] bg-white/90 p-4">
                              <p className="admin-meta-label">User information</p>
                              <div className="mt-3 space-y-2 text-sm text-[#10213A]">
                                <p><strong>Username:</strong> {activity.user?.username || 'Unknown'}</p>
                                {activity.user?.full_name && <p><strong>Full name:</strong> {activity.user.full_name}</p>}
                                {activity.user?.id && <p><strong>User ID:</strong> {activity.user.id}</p>}
                                {activity.session_id && <p className="break-all"><strong>Session ID:</strong> {activity.session_id}</p>}
                              </div>
                            </div>
                            <div className="rounded-[18px] border border-[rgba(28, 77, 136,0.08)] bg-white/90 p-4">
                              <p className="admin-meta-label">Page information</p>
                              <div className="mt-3 space-y-2 text-sm text-[#10213A]">
                                <p><strong>Page:</strong> {getPageName(activity.page_url || '', activity.page_title)}</p>
                                {activity.page_title && <p><strong>Title:</strong> {activity.page_title}</p>}
                                {activity.page_url && <p className="break-all"><strong>URL:</strong> {activity.page_url}</p>}
                                <p><strong>Timestamp:</strong> {new Date(activity.created_at).toLocaleString()}</p>
                              </div>
                            </div>
                            <div className="rounded-[18px] border border-[rgba(28, 77, 136,0.08)] bg-white/90 p-4">
                              <p className="admin-meta-label">Activity details</p>
                              <div className="mt-3 space-y-2 text-sm text-[#10213A]">
                                <p><strong>Type:</strong> {activity.activity_type_display}</p>
                                <p><strong>Time spent:</strong> {activity.time_spent_formatted || '0s'} ({activity.time_spent_seconds}s)</p>
                                {activity.ip_address && <p><strong>IP:</strong> {activity.ip_address}</p>}
                                {activity.action_description && <p><strong>Description:</strong> {activity.action_description}</p>}
                              </div>
                            </div>
                            {((activity.metadata && Object.keys(activity.metadata).length > 0) || (activity.form_data && Object.keys(activity.form_data).length > 0)) && (
                              <div className="rounded-[18px] border border-[rgba(28, 77, 136,0.08)] bg-white/90 p-4">
                                <p className="admin-meta-label">Additional data</p>
                                <div className="mt-3 space-y-3 text-sm text-[#10213A]">
                                  {activity.metadata && Object.keys(activity.metadata).length > 0 && (
                                    <div>
                                      <p className="font-semibold mb-2">Metadata</p>
                                      <pre className="p-3 rounded-[14px] bg-[#F6FBFF] border border-[rgba(28, 77, 136,0.08)] overflow-auto max-h-40 text-xs whitespace-pre-wrap">{JSON.stringify(activity.metadata, null, 2)}</pre>
                                    </div>
                                  )}
                                  {activity.form_data && Object.keys(activity.form_data).length > 0 && (
                                    <div>
                                      <p className="font-semibold mb-2">Form data</p>
                                      <pre className="p-3 rounded-[14px] bg-[#F6FBFF] border border-[rgba(28, 77, 136,0.08)] overflow-auto max-h-40 text-xs whitespace-pre-wrap">{JSON.stringify(activity.form_data, null, 2)}</pre>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </Fragment>
                  );
                })}
              </div>
            )}

            {totalPages > 1 && (
              <div className="mt-6 pt-6 border-t border-[rgba(28, 77, 136,0.08)] flex items-center justify-between gap-4 flex-wrap">
                <button
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                  disabled={page === 1}
                  className="admin-button admin-button-ghost disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="text-sm text-[#4E637D]">Page {page} of {totalPages} ({totalCount} total activities)</span>
                <button
                  onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                  disabled={page === totalPages}
                  className="admin-button admin-button-ghost disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default UserActivityPage;